import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { StatusPill, Skeleton, Input, Button, TabsRoot, TabsList, TabsTrigger, TabsContent } from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { orderMoney, shortDate } from "../orders/order-format.ts";

const TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
] as const;
type Tab = (typeof TABS)[number]["key"];

const EMPTY_COUNTS = { all: 0, active: 0, suspended: 0 };

/** Columns in the table — keeps the skeleton and empty-state `colSpan` honest. */
const COL_COUNT = 7;
const PAGE_SIZE = 25;

function emptyMessage(q: string, tab: Tab): string {
  if (q) return "No buyers match that search.";
  if (tab === "all") return "No buyers yet. A buyer appears here once they place their first order.";
  if (tab === "suspended") return "No suspended buyers.";
  return "No active buyers.";
}

/**
 * Store admin — Buyers directory (Commerce, M3).
 *
 * Read-only: these accounts belong to the buyers who created them from the
 * storefront. The row answers "who is buying, how much, how recently" — the
 * business name opens the buyer's own page for their orders and quotes.
 *
 * **Ordering earns the row, not signing up.** Someone who signs in to see
 * wholesale prices and never buys is not on this screen; they appear on their
 * first order, drafts included. So a row can legitimately read `0 / ₹0 / —`:
 * they have ordered, but nothing is confirmed yet.
 *
 * The module placeholder this replaces promised tier, rep and credit limit.
 * None of those exist in the schema, so none are rendered — an always-"—"
 * column is a dead affordance, not a placeholder for a later slice.
 *
 * @see packages/api-contract/src/procedures/store-buyers.ts
 */
export function BuyersListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);

  // Filtering happens in the query, never on the page: the cursor is
  // filter-relative, so client-side filtering would shrink pages and hand back
  // a cursor that skips rows.
  const list = trpc.storeBuyers.list.useQuery({
    limit: PAGE_SIZE,
    ...(tab === "all" ? {} : { status: tab }),
    ...(q ? { q } : {}),
    ...(cursor ? { cursor } : {}),
  });
  // Tab-independent, so the badges don't flicker when the tab changes.
  const counts = trpc.storeBuyers.counts.useQuery();
  const c = counts.data ?? EMPTY_COUNTS;
  const rows = list.data?.items ?? [];

  const resetPaging = () => setCursors([]);

  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Buyers" }]}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Buyers</h1>
          <p className="mt-1.5 text-sm text-muted">
            <span className="font-mono tabular-nums text-ink">{c.all}</span>{" "}
            {c.all === 1 ? "buyer has" : "buyers have"} ordered from your storefront
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded border border-line bg-paper shadow">
        <TabsRoot
          value={tab}
          onValueChange={(v) => {
            setTab(v as Tab);
            resetPaging();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4">
            <TabsList aria-label="Filter buyers" className="border-b-0 overflow-y-hidden">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} count={c[t.key]}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 py-2">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetPaging();
                }}
                placeholder="Search business / name / phone…"
                aria-label="Search buyers"
                className="h-8 w-full min-w-60 sm:w-72"
              />
            </div>
          </div>
          {/* Each trigger points `aria-controls` at a panel id; the table below
              isn't a Radix panel, so without these the references dangle. */}
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} forceMount className="hidden" aria-hidden />
          ))}
        </TabsRoot>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[940px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-4 text-left font-medium">Buyer</th>
                <th className="px-3 py-4 text-left font-medium">Phone</th>
                <th className="px-3 py-4 text-left font-medium">GSTIN</th>
                <th className="px-3 py-4 text-right font-medium">Orders</th>
                <th className="px-3 py-4 text-right font-medium">Value</th>
                <th className="px-3 py-4 text-left font-medium">Last order</th>
                <th className="px-3 py-4 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-3 py-3" colSpan={COL_COUNT}>
                        <Skeleton className="h-6 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((b) => (
                    <tr key={b.buyerId} className="border-b border-line last:border-0 hover:bg-raised">
                      <td className="px-3 py-3">
                        {/* The business name is the link, not the whole row: a
                            row-wide click target steals text selection from a
                            table an operator reads before they navigate. */}
                        <Link
                          to="/store/admin/buyers/$buyerId"
                          params={{ buyerId: b.buyerId }}
                          className="block font-medium text-ink hover:text-sky-700"
                        >
                          {b.businessName}
                        </Link>
                        <span className="block text-xs text-muted">{b.name}</span>
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">{b.phone}</td>
                      {/* A buyer without a GSTIN gets a B2C invoice — worth
                          seeing at a glance, so it reads "—" rather than blank. */}
                      <td className="px-3 py-3 font-mono text-xs text-ink-2">{b.gstin ?? "—"}</td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {b.orderCount}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {orderMoney(b.orderValuePaise)}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {shortDate(b.lastOrderAt)}
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill tone={b.status === "active" ? "ok" : "bad"} size="sm" dot>
                          {b.status === "active" ? "Active" : "Suspended"}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
              {!list.isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-12 text-center text-sm text-muted">
                    {emptyMessage(q, tab)}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Showing <span className="font-mono font-semibold text-ink">{rows.length}</span>{" "}
            {rows.length === 1 ? "buyer" : "buyers"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              intent="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((p) => p.slice(0, -1))}
            >
              ← Prev
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={!list.data?.nextCursor}
              onClick={() => {
                const next = list.data?.nextCursor;
                if (next) setCursors((p) => [...p, next]);
              }}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>
    </StoreAdminLayout>
  );
}
